import React from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Slide,
  Typography,
} from "@mui/material";

import {
  Logout as LogoutIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import { COLOR_PRIMARY, COLOR_SECONDARY } from "../../../libs/constants";
import { useRouterStore } from "../../../libs/stores";
import { TransitionProps } from "@mui/material/transitions";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface DialogCloseSessionProps {
  open: boolean;
  handleClose: () => void;
}
export const DialogCloseSession = ({
  open,
  handleClose,
}: DialogCloseSessionProps) => {
  const navigate = useNavigate();
  const reset = useRouterStore((state) => state.reset);
  const [isClosing, setClosing] = React.useState(false);
  const [isContinuo, setContinuo] = React.useState(false);

  React.useEffect(() => {
    if (isContinuo) {
      handleCloseSession();
    }
  }, [isContinuo]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCloseSession = () => {
    reset();
    localStorage.removeItem(process.env.REACT_APP_TOKEN!);
    localStorage.removeItem(process.env.REACT_APP_TOKEN_OPTIONS!);
    localStorage.removeItem(process.env.REACT_APP_TOKEN_ROUTER!);
    localStorage.removeItem(process.env.REACT_APP_TOKEN_ROUTER_BACKUP!);
    clearIndexedDB();
    setTimeout(() => {
      navigate("/", { replace: true });
      setClosing(false);
      handleClose();
    }, 2000);
  };

  const handleContinuo = () => {
    setClosing(true);
    setTimeout(() => {
      setContinuo(true);
    }, 1000);
  };

  return (
    <Dialog
      open={open}
      onClose={isClosing ? undefined : handleClose}
      fullWidth
      maxWidth="xs"
      TransitionComponent={Transition}
      aria-labelledby="form-dialog-title"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${COLOR_PRIMARY}, ${COLOR_SECONDARY})`,
          color: "white",
          py: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255,255,255,.18)",
            border: "2px solid rgba(255,255,255,.5)",
          }}
        >
          <LogoutIcon sx={{ fontSize: 32 }} />
        </Box>
        <Typography
          id="form-dialog-title"
          sx={{ fontSize: 20, fontWeight: 700 }}
        >
          Cerrar sesión
        </Typography>
      </Box>
      <DialogContent sx={{ textAlign: "center", pt: 3, pb: 1 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600, color: COLOR_PRIMARY }}>
          ¿Seguro que desea salir?
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", mt: 0.5 }}>
          Su sesión se cerrará y volverá a la pantalla de inicio.
        </Typography>
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            display: "flex",
            gap: 1,
            textAlign: "left",
            borderRadius: 2,
            bgcolor: "#fff8e6",
            border: "1px solid #ffe2a0",
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 20, color: "#b7791f", mt: "1px" }} />
          <Typography sx={{ fontSize: 12.5, color: "#7a5314" }}>
            Se eliminarán los datos guardados localmente en este equipo. Asegúrese
            de haber sincronizado su información pendiente.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5 }}>
        <Button
          onClick={handleClose}
          disabled={isClosing}
          fullWidth
          variant="outlined"
          sx={{
            py: 1,
            fontWeight: 700,
            borderRadius: 2,
            color: COLOR_PRIMARY,
            borderColor: `${COLOR_PRIMARY}66`,
            "&:hover": { borderColor: COLOR_PRIMARY, bgcolor: `${COLOR_PRIMARY}0F` },
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleContinuo}
          disabled={isClosing}
          fullWidth
          variant="contained"
          disableElevation
          sx={{
            py: 1,
            fontWeight: 700,
            borderRadius: 2,
            bgcolor: COLOR_PRIMARY,
            "&:hover": { bgcolor: COLOR_SECONDARY },
          }}
          startIcon={
            isClosing ? (
              <CircularProgress size={18} sx={{ color: "white" }} />
            ) : (
              <LogoutIcon />
            )
          }
        >
          {isClosing ? "Cerrando..." : "Cerrar sesión"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const clearIndexedDB = async () => {
  const dbs = await indexedDB.databases(); // Obtener las bases de datos
  dbs.forEach(({ name }) => {
    console.log(`Deleting database ${name}`);
    if (name) {
      const request = indexedDB.deleteDatabase(name);
      request.onsuccess = () =>
        console.log(`Database ${name} deleted successfully`);
      request.onerror = (error) =>
        console.error(`Failed to delete database ${name}`, error);
    }
  });
};
